import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { SuggestionModal } from "../components/SuggestionModal";
import { ProtonWidget } from "../components/ProtonWidget";
import { applyWindowGlobals, ensurePageCss, readPageData, syncHeadMeta } from "../lib/bootConfig";
import { initProtonWidget } from "../lib/protonWidget";
import { NotFoundPage } from "./NotFoundPage";
import { RADIOS_ORIGIN } from "../lib/origins";

interface SegmentStation {
  id: number;
  title: string;
  href: string;
  location?: string;
  segments?: string;
  slogan?: string;
  description?: string;
}

interface SegmentPageData {
  page?: string;
  slug: string;
  title: string;
  description?: string;
  breadcrumb: string;
  heading: string;
  lead: string;
  listTitle: string;
  howtoTitle: string;
  stations: SegmentStation[];
}

export function SegmentPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const normalizedSlug = slug.trim().toLowerCase();

  const initialData = (() => {
    const preloaded = readPageData<SegmentPageData>();
    if (preloaded && preloaded.page === "segment" && preloaded.slug?.toLowerCase() === normalizedSlug) {
      return preloaded;
    }
    return null;
  })();

  const [data, setData] = useState<SegmentPageData | null>(initialData);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!normalizedSlug) {
      setNotFound(true);
      return;
    }
    const preloaded = readPageData<SegmentPageData>();
    if (preloaded && preloaded.page === "segment" && preloaded.slug?.toLowerCase() === normalizedSlug) {
      setData(preloaded);
      setNotFound(false);
      return;
    }
    let cancelled = false;
    fetch(`/api/v1/pages/segment/${encodeURIComponent(normalizedSlug)}`)
      .then(async (res) => {
        if (!res.ok) {
          if (!cancelled) setNotFound(true);
          return;
        }
        const json = (await res.json()) as SegmentPageData & { ok?: boolean };
        if (cancelled) return;
        if (json.ok === false || !json.slug) {
          setNotFound(true);
        } else {
          setData(json);
          setNotFound(false);
        }
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
  }, [normalizedSlug]);

  useEffect(() => {
    if (!data || notFound) return;
    const canonical = `${RADIOS_ORIGIN}/radios/${data.slug}`;
    const description =
      data.description ||
      "Encontre rádios sertanejas para ouvir online. Compare emissoras, cidades e estilos cadastrados e abra a página da rádio para escolher um stream ao vivo.";
    syncHeadMeta({
      title: data.title,
      description,
      canonical,
      robots: null,
      ogTitle: data.title,
      ogDescription: description,
      ogUrl: canonical,
      ogImage: RADIOS_ORIGIN + "/assets/img/og-image.jpg",
      ogSiteName: "Radios.PRO",
    });
    document.body.className = "rp-body rp-guide-body";
    ensurePageCss("/assets/css/guide.css");
    applyWindowGlobals();
    initProtonWidget();
  }, [data, notFound]);

  if (notFound) {
    return <NotFoundPage />;
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <Header />
      <SuggestionModal />
      <main className="rp-guide-page rp-segment-page">
        <nav className="rp-guide-breadcrumb" aria-label="Navegação estrutural">
          <a href="/">Início</a> <span aria-hidden="true">/</span>{" "}
          <span aria-current="page">{data.breadcrumb}</span>
        </nav>

        <article className="rp-guide-article">
          <header className="rp-guide-header">
            <p className="rp-guide-kicker">Rádios por estilo</p>
            <h1>{data.heading}</h1>
            <p className="rp-guide-lead">{data.lead}</p>
          </header>

          <section className="rp-segment-stations" aria-labelledby="rp-segment-stations-title">
            <h2 id="rp-segment-stations-title">{data.listTitle}</h2>
            <p className="rp-segment-explainer">
              Abra a página da rádio para conferir os streams disponíveis e começar a ouvir. Algumas emissoras oferecem
              mais de uma transmissão.
            </p>
            {data.stations && data.stations.length > 0 ? (
              <ul className="rp-segment-station-list">
                {data.stations.map((st) => (
                  <li key={st.id} className="rp-segment-station-card">
                    <h3>
                      <a href={st.href}>{st.title}</a>
                    </h3>
                    {st.location ? <p className="rp-segment-station-location">{st.location}</p> : null}
                    {st.segments ? (
                      <p className="rp-segment-station-tags">
                        <strong>Estilos cadastrados:</strong> {st.segments}
                      </p>
                    ) : null}
                    {st.slogan ? <p className="rp-segment-station-slogan">{st.slogan}</p> : null}
                    {st.description ? <p className="rp-segment-station-description">{st.description}</p> : null}
                    <a className="rp-segment-station-link" href={st.href}>
                      Ver streams e ouvir
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p role="status">A seleção está temporariamente indisponível. Tente novamente em alguns minutos.</p>
            )}
          </section>

          <section className="rp-segment-howto" aria-labelledby="rp-segment-howto-title">
            <h2 id="rp-segment-howto-title">{data.howtoTitle}</h2>
            <ol className="rp-guide-steps">
              <li>Escolha uma emissora e abra a página dela.</li>
              <li>Confira a cidade, os estilos informados e os streams disponíveis.</li>
              <li>
                Selecione uma transmissão e pressione play. Se não tocar, teste outro stream ou relate o problema na
                página da rádio.
              </li>
            </ol>
          </section>

          <aside className="rp-guide-cta">
            <h2>Explore outras rádios online</h2>
            <p>Pesquise emissoras por nome, cidade ou segmento no catálogo do Radios.PRO.</p>
            <a className="rp-guide-cta-link" href="/">
              Buscar rádios online
            </a>
          </aside>
        </article>
      </main>
      <Footer />
      <ProtonWidget />
    </>
  );
}
